import React, { useState } from "react";
import { motion } from "framer-motion";
import { ShoppingBag, ChevronLeft, ChevronRight } from "lucide-react";
import { useProducts } from "@/hooks/useProducts";
import { useCart } from "@/context/CartContext";
import { optimizedImg, srcSetFor } from "@/lib/img";

/* "Merch That Speaks" hero block — matches the design ref exactly.
 *  - Two vertical rectangular cards side-by-side
 *  - Each card has a stepped pixel notch on the TOP-LEFT corner
 *  - Model photo fills the card (object-cover, no overflow above)
 *  - A wide label band sits ~70% down the card and OVERSHOOTS the right edge
 *    (label color = the OTHER card's panel color — color swap)
 *  - Vertical "MERCH THAT SPEAKS" pixel label on the far left
 *  - Tiny slider progress strip on the bottom right
 *  - SAME LAYOUT on mobile (just scaled) — no stacking
 */

const SLIDE_BLUEPRINTS = [
  [
    {
      productId: "hoodie-varsity-maroon",
      panel: "var(--st-mustard)",
      labelBg: "var(--st-maroon)",
      labelColor: "#F5EBD0",
      tag: "CLASSROOM CRUSH",
    },
    {
      productId: "hoodie-detention-gold",
      panel: "var(--st-maroon)",
      labelBg: "var(--st-mustard)",
      labelColor: "var(--st-fg)",
      tag: "DETENTION GOLD",
    },
  ],
  [
    {
      productId: "sweatshirt-class-in-session",
      panel: "var(--st-mustard)",
      labelBg: "var(--st-maroon)",
      labelColor: "#F5EBD0",
      tag: "ROLL CALL CREW",
    },
    {
      productId: "sweatshirt-roll-call",
      panel: "var(--st-maroon)",
      labelBg: "var(--st-mustard)",
      labelColor: "var(--st-fg)",
      tag: "MAROON HONORS",
    },
  ],
];

function SpeakCard({ entry, idx, isRight }) {
  const { product, panel, labelBg, labelColor, tag } = entry;
  const { add } = useCart();
  const sizes = product.sizes || [];
  const needsSize = sizes.length > 0;
  const [size, setSize] = useState(null);
  const [errKey, setErrKey] = useState(0);

  const handleAdd = () => {
    if (needsSize && !size) {
      setErrKey(Date.now());
      return;
    }
    add(product, size);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.6, delay: idx * 0.1 }}
      className="relative"
      data-testid={`featured-card-${product.id}`}
    >
      {/* Color panel with top-left pixel step notch (image clipped here) */}
      <div
        className="speak-panel relative border-2 border-[#111] overflow-hidden"
        style={{ background: panel, aspectRatio: "3 / 4.2" }}
      >
        <img
          src={optimizedImg(product.image, 900)}
          srcSet={srcSetFor(product.image)}
          sizes="(max-width: 640px) 100vw, 40vw"
          alt={product.name}
          className="absolute inset-0 w-full h-full object-cover object-center select-none"
          draggable={false}
          loading="lazy"
          decoding="async"
        />

        {/* tag chip top-right (inside panel) */}
        <div className="absolute top-2 right-2 z-10 font-price text-base sm:text-base tracking-tight px-2 py-0.5 border-2 border-[#111] bg-[var(--st-cream)] text-[var(--st-fg)] uppercase">
          ${product.price}
        </div>

        {/* OVERSHOOTING LABEL BAND — sits inside the panel. Overshoots
            past the right edge on sm+ for the design flourish; on mobile
            stays inside the card so it never bleeds into the next card. */}
        <div
          className={`absolute left-0 top-[62%] flex items-center border-y-2 border-[#111] z-20 pointer-events-none w-full ${
            isRight ? "sm:w-[140%]" : "sm:w-[135%]"
          }`}
          style={{ background: labelBg, color: labelColor }}
        >
          <div className="font-pixel uppercase tracking-[0.04em] px-3 sm:px-5 py-1.5 sm:py-2 leading-[1.05] whitespace-nowrap overflow-hidden">
            <p className="text-[10px] sm:text-sm md:text-base opacity-80 truncate">{tag}</p>
            <p className="text-base sm:text-xl md:text-2xl truncate">{product.name}</p>
          </div>
        </div>

        {/* DESKTOP/TABLET ACTIONS — pinned to the bottom-right of the panel
            so they don't disturb the magazine layout. Hidden on mobile;
            see the row below for the mobile actions. */}
        <div
          className="hidden sm:flex absolute bottom-2 right-2 z-30 items-center gap-1.5"
          data-testid={`featured-actions-${product.id}`}
        >
          {needsSize && (
            <div
              key={errKey || "ok"}
              className={`flex items-center gap-1 ${errKey ? "animate-shake" : ""}`}
            >
              {sizes.map((s) => {
                const isOn = size === s;
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => {
                      setSize(s);
                      setErrKey(0);
                    }}
                    aria-pressed={isOn}
                    data-testid={`featured-size-${product.id}-${s}`}
                    className={`w-8 h-8 border-2 border-[#111] font-display uppercase text-xs transition-colors shadow-brutal-sm ${
                      isOn
                        ? "bg-[var(--st-maroon)] text-white"
                        : "bg-[var(--st-cream)] text-[var(--st-fg)] hover:bg-[var(--st-mustard)]"
                    }`}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
          )}
          <button
            onClick={handleAdd}
            data-testid={`featured-add-${product.id}`}
            aria-label={`Add ${product.name} to cart`}
            className="w-10 h-10 flex items-center justify-center border-2 border-[#111] bg-[var(--st-cream)] hover:bg-[var(--st-gold)] transition-colors shadow-brutal-sm"
          >
            <ShoppingBag size={14} className="text-[var(--st-fg)]" />
          </button>
        </div>
      </div>

      {/* MOBILE ACTIONS — full-width row directly below the card. Bigger
          tap targets, no overlap, error pill can shake properly. */}
      <div
        className="sm:hidden mt-2 flex items-center gap-1.5"
        data-testid={`featured-actions-mobile-${product.id}`}
      >
        {needsSize && (
          <div
            key={errKey || "ok-m"}
            className={`flex items-center gap-1 flex-1 ${errKey ? "animate-shake" : ""}`}
          >
            {sizes.map((s) => {
              const isOn = size === s;
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => {
                    setSize(s);
                    setErrKey(0);
                  }}
                  aria-pressed={isOn}
                  data-testid={`featured-size-m-${product.id}-${s}`}
                  className={`flex-1 h-10 border-2 border-[#111] font-display uppercase text-sm transition-colors shadow-brutal-sm ${
                    isOn
                      ? "bg-[var(--st-maroon)] text-white"
                      : "bg-[var(--st-cream)] text-[var(--st-fg)]"
                  }`}
                >
                  {s}
                </button>
              );
            })}
          </div>
        )}
        <button
          onClick={handleAdd}
          data-testid={`featured-add-m-${product.id}`}
          aria-label={`Add ${product.name} to cart`}
          className="h-10 px-3 flex items-center justify-center gap-2 border-2 border-[#111] bg-[var(--st-gold)] text-[var(--st-fg)] font-heading uppercase tracking-[0.18em] text-xs shadow-brutal-sm"
        >
          <ShoppingBag size={14} /> Add
        </button>
      </div>
    </motion.div>
  );
}

export default function FeaturedCollections() {
  const [slideIdx, setSlideIdx] = useState(0);
  const { byId } = useProducts();
  const current = SLIDE_BLUEPRINTS[slideIdx]
    .map((entry) => ({ ...entry, product: byId(entry.productId) }))
    .filter((entry) => entry.product);

  return (
    <section
      data-testid="featured-merch-section"
      className="relative bg-[var(--st-bg)] py-12 sm:py-16 md:py-20 border-b-2 border-[#111] overflow-hidden"
    >
      {/* corner crosshairs from design ref */}
      <div className="cross-marker" style={{ top: 40, left: "10%" }} />
      <div className="cross-marker" style={{ top: 40, right: "8%" }} />
      <div className="cross-marker" style={{ bottom: 50, left: "20%" }} />
      <div className="cross-marker" style={{ bottom: 30, right: "16%" }} />

      {/* MOBILE-ONLY horizontal section title. On sm+ the title becomes the
          vertical pixel label on the left edge of the cards grid. */}
      <div className="sm:hidden mb-6 px-4 text-center">
        <p
          data-testid="merch-speaks-title-mobile"
          className="font-display uppercase tracking-tight text-[var(--st-fg)] text-3xl leading-none"
        >
          Merch That Speaks
        </p>
        <span className="mt-2 inline-block w-12 h-[3px] bg-[var(--st-maroon)]" />
      </div>

      <div className="max-w-[1400px] mx-auto px-4 sm:pl-4 md:pl-8 sm:pr-0 grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-3 md:gap-4 items-stretch">
        {/* VERTICAL LABEL on the left — sm+ only (mobile uses the centred
            title above instead) */}
        <div className="hidden sm:flex sm:col-span-1 items-center justify-center">
          <p
            data-testid="merch-speaks-label"
            className="font-display uppercase text-[var(--st-fg)] tracking-tight whitespace-nowrap text-base sm:text-2xl md:text-3xl lg:text-4xl leading-none"
            style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
          >
            Merch That Speaks
          </p>
        </div>

        {/* TWO CARDS — stacked on mobile (full width), side-by-side from sm+. */}
        <div className="sm:col-span-11 grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-3 md:gap-4 relative pr-0 sm:pr-4 overflow-hidden">
          {current.map((entry, idx) => (
            <SpeakCard
              key={entry.product.id + slideIdx}
              entry={entry}
              idx={idx}
              isRight={idx === 1}
            />
          ))}
        </div>
      </div>

      {/* Slider control — centred on mobile, right-aligned from sm+. */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 mt-6 sm:mt-6 flex justify-center sm:justify-end items-center gap-2">
        <button
          type="button"
          onClick={() =>
            setSlideIdx((i) => (i - 1 + SLIDE_BLUEPRINTS.length) % SLIDE_BLUEPRINTS.length)
          }
          aria-label="Previous slide"
          data-testid="featured-prev"
          className="w-9 h-9 flex items-center justify-center border-2 border-[#111] bg-[var(--st-cream)] hover:bg-[var(--st-mustard)] transition-colors shadow-brutal-sm"
        >
          <ChevronLeft size={16} />
        </button>
        <div className="flex items-center gap-2 px-1">
          {SLIDE_BLUEPRINTS.map((_, i) => (
            <button
              key={i}
              onClick={() => setSlideIdx(i)}
              aria-label={`Slide ${i + 1}`}
              data-testid={`featured-slide-${i}`}
              className={`h-2 border-2 border-[#111] transition-all ${
                slideIdx === i ? "w-10 bg-[var(--st-mustard)]" : "w-5 bg-[var(--st-muted)]"
              }`}
            />
          ))}
        </div>
        <button
          type="button"
          onClick={() => setSlideIdx((i) => (i + 1) % SLIDE_BLUEPRINTS.length)}
          aria-label="Next slide"
          data-testid="featured-next"
          className="w-9 h-9 flex items-center justify-center border-2 border-[#111] bg-[var(--st-mustard)] hover:bg-[var(--st-gold)] transition-colors shadow-brutal-sm"
        >
          <ChevronRight size={16} />
        </button>
      </div>
    </section>
  );
}
